import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader2,
  Play,
  FileText,
  Link2,
  CheckCircle2,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Plus,
  X
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { Badge } from '../ui/badge'
import { Label } from '../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { supabase } from '../../lib/supabaseClient'
import {
  unassignPrograma,
  iniciarPrograma,
  getTrainingProgramsLibrary,
  assignProgramaToSesiones
} from '../../lib/supabase'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import ResumenRecorrido from './ResumenRecorrido'

const ProgramaAsignadoCard = ({ sesionId, onChange }) => {
  const navigate = useNavigate()
  const { staff } = useAuth()
  const { canManagePrograms } = usePermissions()
  const [loading, setLoading] = useState(true)
  const [sesionInfo, setSesionInfo] = useState(null) // { fecha, grupo_id }
  const [asignacion, setAsignacion] = useState(null)
  const [estado, setEstado] = useState(null)
  const [recorrido, setRecorrido] = useState(null)
  const [busy, setBusy] = useState(false)
  const [resumenAbierto, setResumenAbierto] = useState(false)
  const [asignandoAbierto, setAsignandoAbierto] = useState(false)
  const [programasDisponibles, setProgramasDisponibles] = useState([])
  const [cargandoProgramas, setCargandoProgramas] = useState(false)
  const [programaSeleccionado, setProgramaSeleccionado] = useState('')
  const [errorAsignar, setErrorAsignar] = useState(null)
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()

  const load = async () => {
    if (!sesionId) {
      setLoading(false)
      return
    }
    const { data } = await supabase
      .from('sesiones')
      .select(`
        id,
        fecha,
        grupo_id,
        programa_estado_en_vivo,
        programa_recorrido,
        programa_asignado_id,
        asignacion:programas_entrenamiento_agenda!sesiones_programa_asignado_id_fkey (
          id,
          fecha,
          grupo_sync_token,
          programa:programas_entrenamiento (
            id, titulo, descripcion, deportes,
            secciones:programa_secciones(id, duracion_seg)
          )
        )
      `)
      .eq('id', sesionId)
      .maybeSingle()

    setSesionInfo(data ? { fecha: data.fecha, grupo_id: data.grupo_id } : null)
    setAsignacion(data?.asignacion || null)
    setEstado(data?.programa_estado_en_vivo || null)
    setRecorrido(data?.programa_recorrido || null)
    setLoading(false)
  }

  const cargarProgramasParaAsignar = async () => {
    setCargandoProgramas(true)
    setErrorAsignar(null)
    const { data, error } = await getTrainingProgramsLibrary()
    setCargandoProgramas(false)
    if (error) {
      setErrorAsignar('No pudimos cargar la biblioteca de programas')
      return
    }
    // Solo programas con secciones (los unicos que se pueden ejecutar en vivo)
    const conSecciones = (data || []).filter((p) => p.secciones && p.secciones.length > 0)
    setProgramasDisponibles(conSecciones)
  }

  const handleAbrirAsignar = async () => {
    setAsignandoAbierto(true)
    setProgramaSeleccionado('')
    await cargarProgramasParaAsignar()
  }

  const handleAsignar = async () => {
    if (!programaSeleccionado) {
      setErrorAsignar('Selecciona un programa')
      return
    }
    if (!sesionInfo?.fecha || !sesionInfo?.grupo_id) {
      setErrorAsignar('No pudimos identificar la sesion')
      return
    }
    setBusy(true)
    setErrorAsignar(null)
    const { error } = await assignProgramaToSesiones({
      programaId: programaSeleccionado,
      fechas: [sesionInfo.fecha],
      grupoIds: [sesionInfo.grupo_id],
      relojCompartido: false,
      staffId: staff?.id || null
    })
    setBusy(false)
    if (error) {
      setErrorAsignar(error.message || 'No pudimos asignar el programa')
      return
    }
    setAsignandoAbierto(false)
    if (onChange) onChange()
    await load()
  }

  useEffect(() => {
    let cancelled = false
    const wrapped = async () => {
      if (cancelled) return
      await load()
    }
    wrapped()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId])

  if (loading) {
    return (
      <Card>
        <CardContent className="py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Verificando programa asignado...
        </CardContent>
      </Card>
    )
  }

  // Empty state: no hay asignacion. Si el coach tiene permiso, mostrar
  // boton para asignar inline desde esta misma pantalla.
  if (!asignacion) {
    if (!canManagePrograms) {
      // coaches sin permiso no ven nada (no rompe el flujo de asistencia)
      return null
    }
    return (
      <>
        <Card className="border-dashed border-input bg-muted/30">
          <CardContent className="py-3 space-y-2">
            {!asignandoAbierto ? (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>Sin programa asignado para esta sesión</span>
                </div>
                <Button size="sm" variant="outline" onClick={handleAbrirAsignar}>
                  <Plus className="h-4 w-4 mr-1" />
                  Asignar programa
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">
                    Asignar programa a esta sesion
                  </p>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setAsignandoAbierto(false)}
                    aria-label="Cancelar"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {errorAsignar && (
                  <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                    {errorAsignar}
                  </div>
                )}

                <div>
                  <Label htmlFor="programa-select">Programa</Label>
                  {cargandoProgramas ? (
                    <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Cargando biblioteca...
                    </div>
                  ) : programasDisponibles.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">
                      No hay programas con secciones cronometradas en la biblioteca. Crea uno primero en{' '}
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => navigate('/programas/biblioteca')}
                      >
                        Programas → Biblioteca
                      </button>
                      .
                    </p>
                  ) : (
                    <Select
                      value={programaSeleccionado || undefined}
                      onValueChange={setProgramaSeleccionado}
                    >
                      <SelectTrigger id="programa-select" className="mt-1 w-full">
                        <SelectValue placeholder="Selecciona un programa..." />
                      </SelectTrigger>
                      <SelectContent>
                        {programasDisponibles.map((p) => {
                          const totalMin = Math.floor(
                            p.secciones.reduce((s, x) => s + (x.duracion_seg || 0), 0) / 60
                          )
                          return (
                            <SelectItem key={p.id} value={p.id}>
                              {p.titulo} · {totalMin} min · {p.secciones.length} secciones
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAsignandoAbierto(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAsignar}
                    disabled={busy || !programaSeleccionado || programasDisponibles.length === 0}
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                    Asignar
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <ConfirmDialog />
      </>
    )
  }


  const programa = asignacion.programa
  const secciones = programa?.secciones || []
  const tieneSecciones = secciones.length > 0
  const duracionMin = Math.floor(secciones.reduce((s, x) => s + (x.duracion_seg || 0), 0) / 60)
  const enCurso = Boolean(estado?.iniciado_en && !estado?.terminado_en)
  const yaEjecutado = Boolean(recorrido) && !enCurso

  const handleIniciar = () => {
    navigate(`/entrenamiento/${sesionId}/en-vivo`)
  }

  const handleReiniciar = async () => {
    const fechaPrev = recorrido?.terminado_en
      ? format(parseISO(recorrido.terminado_en), "EEEE d 'de' MMMM 'a las' HH:mm", { locale: es })
      : 'antes'
    const ok = await confirm(
      'Reiniciar entrenamiento',
      `Este programa ya se ejecuto ${fechaPrev}. Si lo reinicias, el resumen actual se sobrescribira con el del nuevo recorrido.\n\n¿Continuar?`,
      { destructive: true, confirmText: 'Reiniciar' }
    )
    if (!ok) return

    setBusy(true)
    const { error } = await iniciarPrograma({
      sesionId,
      programaId: programa.id,
      syncToken: asignacion.grupo_sync_token || null
    })
    setBusy(false)

    if (error) {
      await alert('Error', error.message || 'No pudimos reiniciar')
      return
    }
    navigate(`/entrenamiento/${sesionId}/en-vivo`)
  }

  const handleQuitar = async () => {
    const ok = await confirm(
      'Quitar programa asignado',
      `¿Quitar el programa "${programa?.titulo}" de esta sesion?`,
      { destructive: true, confirmText: 'Quitar' }
    )
    if (!ok) return
    setBusy(true)
    const { error } = await unassignPrograma(asignacion.id)
    setBusy(false)
    if (error) {
      await alert('Error', error.message)
      return
    }
    if (onChange) onChange()
    setAsignacion(null)
  }

  return (
    <>
      <Card
        className={
          yaEjecutado
            ? 'border-green-200 bg-green-50/30'
            : 'border-blue-200 bg-blue-50/30'
        }
      >
        <CardContent className="py-3 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {yaEjecutado ? (
                  <CheckCircle2 className="h-4 w-4 text-green-700" />
                ) : (
                  <FileText className="h-4 w-4 text-blue-700" />
                )}
                <p
                  className={`text-sm font-semibold ${
                    yaEjecutado ? 'text-green-900' : 'text-blue-900'
                  }`}
                >
                  {yaEjecutado ? 'Entrenamiento completado: ' : 'Programa asignado: '}
                  {programa?.titulo}
                </p>
              </div>
              {tieneSecciones && (
                <p
                  className={`text-xs mt-0.5 ${
                    yaEjecutado ? 'text-green-700/80' : 'text-blue-700/80'
                  }`}
                >
                  ⏱ {duracionMin} min · {secciones.length} secciones
                  {yaEjecutado && recorrido?.terminado_en && (
                    <>
                      {' · '}
                      Terminado{' '}
                      {format(
                        parseISO(recorrido.terminado_en),
                        "EEE d 'a las' HH:mm",
                        { locale: es }
                      )}
                    </>
                  )}
                </p>
              )}
              {asignacion.grupo_sync_token && (
                <Badge
                  variant="outline"
                  className={`mt-1 text-[10px] ${
                    yaEjecutado
                      ? 'border-green-300 text-green-700'
                      : 'border-blue-300 text-blue-700'
                  }`}
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Sincronizado entre grupos
                </Badge>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {!tieneSecciones && (
              <Button variant="outline" size="sm" disabled>
                Programa sin secciones cronometradas
              </Button>
            )}

            {tieneSecciones && yaEjecutado && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setResumenAbierto((p) => !p)}
                >
                  {resumenAbierto ? (
                    <ChevronUp className="h-4 w-4 mr-1" />
                  ) : (
                    <ChevronDown className="h-4 w-4 mr-1" />
                  )}
                  {resumenAbierto ? 'Ocultar resumen' : 'Ver resumen'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleReiniciar}
                  disabled={busy}
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-1" />
                  )}
                  Reiniciar
                </Button>
              </>
            )}

            {tieneSecciones && !yaEjecutado && (
              <Button onClick={handleIniciar} size="sm">
                <Play className="h-4 w-4 mr-1" />
                {enCurso ? 'Unirse al entrenamiento en vivo' : 'Iniciar Modo entrenamiento'}
              </Button>
            )}

            {!enCurso && !yaEjecutado && (
              <Button variant="ghost" size="sm" onClick={handleQuitar} disabled={busy}>
                {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Quitar
              </Button>
            )}
          </div>

          {/* Resumen inline (solo cuando ya ejecutado y expandido) */}
          {yaEjecutado && resumenAbierto && (
            <div className="pt-3 border-t border-green-200 mt-2">
              <ResumenRecorrido recorrido={recorrido} compacto />
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog />
    </>
  )
}

export default ProgramaAsignadoCard
